import { formatBytes, formatDate } from "@elektr/shared";
import { useQuery } from "@tanstack/react-query";

import { Badge, Card, Spinner } from "@/components/ui/card";
import { api, unwrap } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

function Meter({
  label,
  percent,
  limit,
  detail,
}: {
  label: string;
  percent: number | null;
  limit: number;
  detail?: string;
}) {
  const { t } = useT();
  const over = percent != null && percent >= limit;
  return (
    <div>
      <div className="mb-1 flex justify-between text-sm">
        <span>{label}</span>
        <span className={cn("font-semibold", over && "text-danger")}>
          {percent == null ? t("server.sys_unknown") : `${String(percent).replace(".", ",")}%`}
        </span>
      </div>
      <div
        className="relative h-2.5 rounded-full bg-muted"
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent ?? 0}
      >
        <div
          className={cn("h-full rounded-full", over ? "bg-danger" : "bg-primary")}
          style={{ width: `${Math.min(percent ?? 0, 100)}%` }}
        />
        <div
          className="absolute inset-y-0 w-px bg-foreground/40"
          style={{ left: `${limit}%` }}
          aria-hidden
        />
      </div>
      {detail && <div className="mt-0.5 text-xs text-muted-foreground">{detail}</div>}
    </div>
  );
}

/** CPU, RAM, disk: host-agent bo'lsa host qiymatlari. 85% dan oshsa panel ogohlantiradi. */
export function SystemCard() {
  const { t } = useT();
  const q = useQuery({
    queryKey: ["hosting", "system"],
    queryFn: async () => unwrap(await api.GET("/api/hosting/system/")),
    refetchInterval: 60_000,
  });
  const s = q.data;
  return (
    <Card data-testid="system">
      <h2 className="mb-3 font-bold">{t("server.sys_title")}</h2>
      {!s ? (
        <Spinner />
      ) : (
        <div className="space-y-3">
          <Meter
            label={t("server.sys_cpu")}
            percent={s.cpu_percent}
            limit={s.limit_percent}
            detail={`${s.cpu_count} vCPU`}
          />
          <Meter
            label={t("server.sys_ram")}
            percent={s.mem_percent}
            limit={s.limit_percent}
            detail={
              s.mem_total != null && s.mem_used != null
                ? `${formatBytes(s.mem_used)} / ${formatBytes(s.mem_total)}`
                : undefined
            }
          />
          <Meter label={t("server.sys_disk")} percent={s.disk_percent} limit={s.limit_percent} />
          <p className="text-xs text-muted-foreground">
            {t("server.sys_limit", { n: String(s.limit_percent) })}
            {s.source === "local" ? ` · ${t("server.sys_local")}` : ""}
          </p>
        </div>
      )}
    </Card>
  );
}

/** Oxirgi zaxira va tiklash testi natijasi (skript yozgan holatdan). */
export function BackupsCard() {
  const { t } = useT();
  const q = useQuery({
    queryKey: ["hosting", "backups"],
    queryFn: async () => unwrap(await api.GET("/api/hosting/backups/")),
  });
  const b = q.data;
  const row = (label: string, value: string, ok?: boolean) => (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="flex items-center gap-2 text-right">
        {ok !== undefined && (
          <Badge tone={ok ? "success" : "danger"}>
            {ok ? t("server.backup_ok") : t("server.backup_fail")}
          </Badge>
        )}
        {value}
      </span>
    </div>
  );
  return (
    <Card data-testid="backups">
      <h2 className="mb-3 font-bold">{t("server.backup_title")}</h2>
      {!b ? (
        <Spinner />
      ) : !b.configured ? (
        <p className="text-sm text-muted-foreground">{t("server.backup_not_configured")}</p>
      ) : !b.last_backup ? (
        <p className="text-sm text-warning">{t("server.backup_none")}</p>
      ) : (
        <div className="space-y-2">
          {row(t("server.backup_last"), formatDate(b.last_backup.finished_at), b.last_backup.ok)}
          {!b.last_backup.ok && b.last_backup.error && (
            <p role="alert" className="text-sm text-danger">
              {t("server.backup_error", { msg: b.last_backup.error })}
            </p>
          )}
          {row(
            t("server.backup_last_ok"),
            b.last_ok_backup_at ? formatDate(b.last_ok_backup_at) : "—",
          )}
          {b.last_backup.size_bytes > 0 &&
            row(t("server.backup_size"), formatBytes(b.last_backup.size_bytes))}
          {b.last_restore_test ? (
            <>
              {row(
                t("server.backup_restore"),
                formatDate(b.last_restore_test.finished_at),
                b.last_restore_test.ok,
              )}
              {!b.last_restore_test.ok && b.last_restore_test.error && (
                <p role="alert" className="text-sm text-danger">
                  {t("server.backup_error", { msg: b.last_restore_test.error })}
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">{t("server.backup_restore_none")}</p>
          )}
        </div>
      )}
    </Card>
  );
}
